// LaserCutInterlockingPlanar2.js - Full Enhanced Version
import React, {
  useEffect,
  useRef,
  useState,
  useCallback,
  useMemo,
} from "react";
import * as THREE from "three";
import { STLLoader } from "three/examples/jsm/loaders/STLLoader";
import { sliceModelXZ, sliceModelYZ } from "../utils/sliceModel";
import { initializeThreeScene } from "../utils/initializeThreeScene";
import { Brush, Evaluator, ADDITION, SUBTRACTION } from "three-bvh-csg";
import useVisualHelpers from "../hooks/useVisualHelpers";
//import TutorialWindow from '../components/TutorialWindow';
//import { laserCutInterlockingTutorial } from '../utils/tutorialContents';
import { useSyncedCamera } from "../hooks/useSyncedCamera";
//import { downloadSVG } from '../utils/exportUtils';
import { normalizeModel } from "../utils/normalizeModel";
import "./Example1FloatingPart.css";
import { Face } from "dxf-writer";

import { driver } from "driver.js";
import "driver.js/dist/driver.css";

const calculateIntersections = (
  horizontalContours,
  verticalContours,
  materialThickness
) => {
  const result = { lines: [], slots: { horizontal: [], vertical: [] } };
  horizontalContours.forEach((hContour, hIndex) => {
    verticalContours.forEach((vContour, vIndex) => {
      const hY = hContour.y;
      const vX = vContour.x;
      const verticalPoints = vContour.points;
      const yCoords = verticalPoints.map((p) => p.x);
      const minY = Math.min(...yCoords);
      const maxY = Math.max(...yCoords);
      if (minY <= hY && hY <= maxY) {
        const projectedPoints = [];
        for (let i = 0; i < verticalPoints.length; i++) {
          const curr = verticalPoints[i];
          const next = verticalPoints[(i + 1) % verticalPoints.length];
          if ((curr.x - hY) * (next.x - hY) > 0) continue;
          const t = (hY - curr.x) / (next.x - curr.x);
          const z = curr.y + t * (next.y - curr.y);
          projectedPoints.push(new THREE.Vector2(vX, z));
        }
        if (projectedPoints.length >= 2) {
          projectedPoints.sort((a, b) => a.y - b.y);
          const [firstPoint, lastPoint] = [
            projectedPoints[0],
            projectedPoints.at(-1),
          ];
          const direction = new THREE.Vector2()
            .subVectors(lastPoint, firstPoint)
            .normalize();
          const offset = direction
            .clone()
            .multiplyScalar(materialThickness * 0.5);
          const start = new THREE.Vector2().copy(firstPoint).sub(offset);
          const end = new THREE.Vector2().copy(lastPoint).add(offset);
          const lineStart = new THREE.Vector3(vX, hY, start.y);
          const lineEnd = new THREE.Vector3(vX, hY, end.y);
          result.lines.push({ start: lineStart, end: lineEnd, hIndex, vIndex });
          const mid = new THREE.Vector3()
            .addVectors(lineStart, lineEnd)
            .multiplyScalar(0.5);
          const hSlot = {
            center: new THREE.Vector3()
              .addVectors(lineStart, mid)
              .multiplyScalar(0.5),
            length: lineStart.distanceTo(mid),
            width: materialThickness * 1.1,
            height: materialThickness * 1.1,
            direction: new THREE.Vector3(0, 0, 1),
            hIndex,
            vIndex,
          };
          const vSlot = {
            center: new THREE.Vector3()
              .addVectors(mid, lineEnd)
              .multiplyScalar(0.5),
            length: mid.distanceTo(lineEnd),
            width: materialThickness * 1.1,
            height: materialThickness * 1.1,
            direction: new THREE.Vector3(0, 1, 0),
            hIndex,
            vIndex,
          };
          result.slots.horizontal.push(hSlot);
          result.slots.vertical.push(vSlot);
        }
      }
    });
  });
  return result;
};

const applySlotsToParts = (
  horizontalPlanes,
  verticalPlanes,
  slots,
  materialThickness
) => {
  const evaluator = new Evaluator();
  const apply = (planes, slotList, isVertical) => {
    planes.forEach((plane, index) => {
      const planeSlots = slotList.filter(
        (s) => (isVertical ? s.vIndex : s.hIndex) === index
      );
      if (!planeSlots.length) return;
      const planeBrush = new Brush(plane.geometry);
      planeBrush.position.copy(plane.position);
      planeBrush.rotation.copy(plane.rotation);
      planeBrush.scale.copy(plane.scale);
      planeBrush.updateMatrixWorld(true);
      const slotBrushes = planeSlots.map((slot) => {
        const box = new THREE.BoxGeometry(
          isVertical ? slot.width : slot.width,
          isVertical ? slot.length : slot.height,
          isVertical ? slot.height : slot.length
        );
        const b = new Brush(box);
        b.position.copy(slot.center);
        b.lookAt(new THREE.Vector3().addVectors(slot.center, slot.direction));
        b.updateMatrixWorld(true);
        return b;
      });
      let combined = slotBrushes[0];
      for (let i = 1; i < slotBrushes.length; i++) {
        combined = evaluator.evaluate(combined, slotBrushes[i], ADDITION);
      }
      const resultEval = evaluator.evaluate(planeBrush, combined, SUBTRACTION);
      const inverseMatrix = new THREE.Matrix4()
        .copy(plane.matrixWorld)
        .invert();
      resultEval.geometry.applyMatrix4(inverseMatrix);
      plane.geometry.dispose();
      plane.geometry = resultEval.geometry;
    });
  };
  apply(horizontalPlanes, slots.horizontal, false);
  apply(verticalPlanes, slots.vertical, true);
};

const Example1FloatingPart = ({ stlFile }) => {
  const [hoveredRegion, setHoveredRegion] = useState(null);
const [driverObj, setDriverObj] = useState(null);
  const [currentStep, setCurrentStep] = useState(1); // Step 1 ~ 4
  const [highlightRegions, setHighlightRegions] = useState([]);
const [feedbackMessage, setFeedbackMessage] = useState("");
const [highlightedWorkflow, setHighlightedWorkflow] = useState(null); // ← 新加这行
  const [raycaster] = useState(() => new THREE.Raycaster());
  const pointer = useRef(new THREE.Vector2());

  const containerRef = useRef(null);
  const sceneRef = useRef(null);
  const rendererRef = useRef(null);
  const cameraRef = useRef(null);
  const controlsRef = useRef(null);
  const modelRef = useRef(null);
  const regionsRef = useRef([]);

  const verticalPlanes = useRef([]);
  const horizontalPlanes = useRef([]);
  const verticalContours = useRef([]);
  const horizontalContours = useRef([]);

  const { createVisualization } = useVisualHelpers();
  // const [tutorialVisible, setTutorialVisible] = useState(false);
  const [expanded, setExpanded] = useState(false);
  const [materialThickness, setMaterialThickness] = useState(3);
  const [cutNumber, setCutNumber] = useState(6);
  // const [svgReady, setSvgReady] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState(null);

  const pageId = useMemo(() => `interlocking-planar-2`, []);
  useSyncedCamera(pageId, cameraRef, controlsRef);
  

  const buildClosedCurveWorld = (segments, uniqueVertices, matrixWorld) => {
    const worldVertices = uniqueVertices.map((v) =>
      v.clone().applyMatrix4(matrixWorld)
    );
    const used = new Set();
    const curve = [];
    let current = segments[0].i1;
    curve.push(worldVertices[current]);
    let found = true;
    let safety = 0;
    while (found && safety < segments.length * 2) {
      found = false;
      for (let i = 0; i < segments.length; i++) {
        if (used.has(i)) continue;
        const s = segments[i];
        if (s.i1 === current) {
          current = s.i2;
          curve.push(worldVertices[current]);
          used.add(i);
          found = true;
          break;
        } else if (s.i2 === current) {
          current = s.i1;
          curve.push(worldVertices[current]);
          used.add(i);
          found = true;
          break;
        }
      }
      safety++;
    }
    if (!curve[0].equals(curve[curve.length - 1])) curve.push(curve[0].clone());
    return curve;
  };

  const generateSlices = useCallback(() => {
    if (!modelRef.current || !sceneRef.current) return;
    verticalPlanes.current = [];
    horizontalPlanes.current = [];
    verticalContours.current = [];
    horizontalContours.current = [];

    const model = modelRef.current;
    model.updateMatrixWorld(true);
    const bbox = new THREE.Box3().setFromObject(model);

    const verticalX = Array.from(
      { length: cutNumber + 2 },
      (_, i) => bbox.min.x + (i / (cutNumber + 1)) * (bbox.max.x - bbox.min.x)
    );

    const horizontalY = Array.from(
      { length: cutNumber + 2 },
      (_, i) => bbox.min.y + (i / (cutNumber + 1)) * (bbox.max.y - bbox.min.y)
    );

    verticalX.forEach((x, i) => {
      const result = sliceModelYZ(model, null, x);
      if (!result?.componentSegments) return;
      result.componentSegments.forEach((segs, j) => {
        if (segs.length < 2) return;
        const pts = buildClosedCurveWorld(
          segs,
          result.uniqueVertices,
          model.matrixWorld
        );
        if (pts.length < 3) return;
        const shape = new THREE.Shape(
          pts.map((p) => new THREE.Vector2(p.y, p.z))
        );
        const geom = new THREE.ExtrudeGeometry(shape, {
          depth: materialThickness,
          bevelEnabled: false,
        });
        const mesh = new THREE.Mesh(
          geom,
          new THREE.MeshStandardMaterial({
            color: 0xcd853f,
            side: THREE.DoubleSide,
          })
        );
        mesh.rotation.y = Math.PI / 2;
        mesh.rotation.x = Math.PI / 2;
        mesh.position.x = x - materialThickness / 2;
        mesh.updateMatrixWorld(true);
        sceneRef.current.add(mesh);
        verticalPlanes.current.push(mesh);
        verticalContours.current.push({ x, points: shape.getPoints() });
      });
    });

    horizontalY.forEach((y, i) => {
      const result = sliceModelXZ(model, null, y);
      if (!result?.componentSegments) return;
      result.componentSegments.forEach((segs, j) => {
        if (segs.length < 2) return;
        const pts = buildClosedCurveWorld(
          segs,
          result.uniqueVertices,
          model.matrixWorld
        );
        if (pts.length < 3) return;
        const shape = new THREE.Shape(
          pts.map((p) => new THREE.Vector2(p.x, p.z))
        );
        const geom = new THREE.ExtrudeGeometry(shape, {
          depth: materialThickness,
          bevelEnabled: false,
        });
        const mesh = new THREE.Mesh(
          geom,
          new THREE.MeshStandardMaterial({
            color: 0xf5deb3,
            side: THREE.DoubleSide,
          })
        );
        mesh.rotation.x = Math.PI / 2;
        mesh.position.y = y + materialThickness / 2;
        mesh.updateMatrixWorld(true);
        sceneRef.current.add(mesh);
        horizontalPlanes.current.push(mesh);
        horizontalContours.current.push({ y, points: shape.getPoints() });
      });
    });

    const { slots } = calculateIntersections(
      horizontalContours.current,
      verticalContours.current,
      materialThickness
    );
    applySlotsToParts(
      horizontalPlanes.current,
      verticalPlanes.current,
      slots,
      materialThickness
    );
    // setSvgReady(true);
  }, [materialThickness, cutNumber]);

  const initScene = useCallback(() => {
    const cleanup = initializeThreeScene(
      containerRef,
      sceneRef,
      rendererRef,
      cameraRef,
      controlsRef
    );
    sceneRef.current.add(new THREE.GridHelper(200, 20, 0x444444, 0x888888));
    sceneRef.current.add(new THREE.AxesHelper(50));
    return cleanup;
  }, []);

  const loadModel = useCallback(() => {
    setIsLoading(true);
    const loader = new STLLoader();
    const path = "/models/twistT.stl"; // 固定使用bunny模型
    loader.load(
      path,
      (geometry) => {
        geometry.computeBoundingBox();
        const mesh = new THREE.Mesh(
          geometry,
          new THREE.MeshNormalMaterial({ transparent: true, opacity: 0.3 })
        );
        geometry.translate(
          -geometry.boundingBox.getCenter(new THREE.Vector3()).x,
          -geometry.boundingBox.getCenter(new THREE.Vector3()).y,
          -geometry.boundingBox.getCenter(new THREE.Vector3()).z
        );
        geometry.rotateX(-Math.PI / 2);
        geometry.rotateY(-Math.PI / 2);
        const bbox = new THREE.Box3().setFromObject(mesh);
        mesh.position.y -= bbox.min.y;
        mesh.updateMatrixWorld(true);
        modelRef.current = mesh;
        //  sceneRef.current.add(mesh);
        createVisualization(mesh, sceneRef.current, bbox);
        setTimeout(() => {
          generateSlices();
          setIsLoading(false);
        }, 50);
      },
      undefined,
      (err) => {
        setError("Failed to load STL");
        setIsLoading(false);
      }
    );
  }, [generateSlices]);

  const handlePointerMove = (event) => {
    const rect = rendererRef.current.domElement.getBoundingClientRect();
    pointer.current.x = ((event.clientX - rect.left) / rect.width) * 2 - 1;
    pointer.current.y = -((event.clientY - rect.top) / rect.height) * 2 + 1;

    raycaster.setFromCamera(pointer.current, cameraRef.current);
    const intersects = raycaster.intersectObjects(regionsRef.current, false);

    if (intersects.length > 0) {
      const hovered = intersects[0].object;
      if (hovered !== hoveredRegion) {
        if (hoveredRegion) {
          // 恢复上一个 hover 小球颜色
          hoveredRegion.material.color.set(
            hoveredRegion.userData.isCorrect ? 0x00ff00 : 0xff0000
          );
        }
        hovered.material.color.set(0xffff00); // 高亮为黄色
        setHoveredRegion(hovered);
      }
    } else {
      if (hoveredRegion) {
        hoveredRegion.material.color.set(
          hoveredRegion.userData.isCorrect ? 0x00ff00 : 0xff0000
        );
        setHoveredRegion(null);
      }
    }
  };

  // 高亮特定workflow的函数
const highlightWorkflow = useCallback((workflowId, title, keywords, explanation) => {
  if (driverObj) {
    driverObj.highlight({
      element: `[data-workflow="${workflowId}"]`,
      popover: {
        side: "right",
        title: title,
        description: `Key advantages: ${keywords.join(', ')}\n\n${explanation}`,
        nextBtnText: "Got it!",
      }
    });
    
    // 高亮关键词
    highlightKeywords(keywords);
    setHighlightedWorkflow(workflowId);
  }
}, [driverObj]);

// 高亮关键词的函数
const highlightKeywords = useCallback((keywords) => {
  keywords.forEach(keyword => {
    const chipElement = document.querySelector(`[data-keyword="${keyword}"]`);
    if (chipElement) {
      chipElement.classList.add('highlight-pulse');
      setTimeout(() => {
        chipElement.classList.remove('highlight-pulse');
      }, 4000);
    }
  });
}, []);

// 显示解决方案对比的函数
// 显示解决方案对比的函数
const showSolutionComparison = useCallback((solutionType) => {
  const solutions = {
    'moldmaking': {
      workflowId: 'FoundationalWorkflows_Mold making',
      title: '🏺 Mold Making Solution',
      keywords: ['Silicone', 'Precision', 'Replication'],
      explanation: 'Create high-resolution, complete shapes using silicone molds. Perfect for complex geometry but results in soft, flexible materials.'
    },
    'stacked-layers': {
      workflowId: 'FoundationalWorkflows_Laser cut stacked sheets (Algorithm 1)',
      title: '📚 Stacked Layers Approach',
      keywords: ['Easy to Assemble', 'Wood'],
      explanation: 'Layer-by-layer construction preserves more of the original shape but uses more material and results in heavier objects.'
    },
    '3d-printing': {
      workflowId: 'FoundationalWorkflows_GCode Generator', 
      title: '🖨️ 3D Printing Solution',
      keywords: ['Precise', 'Plastic'],
      explanation: '3D printing handles complex floating geometry perfectly, preserving all details in rigid plastic material.'
    }
  };

  const solution = solutions[solutionType];
  if (solution) {
    highlightWorkflow(solution.workflowId, solution.title, solution.keywords, solution.explanation);
  }
}, [highlightWorkflow]);

  useEffect(() => {
    const cleanup = initScene();
    loadModel();
    return cleanup;
  }, [initScene, loadModel]);


  // 新增：步骤3.52时启动driver.js高亮sidebar
// 初始化 driver.js 对象
useEffect(() => {
  const newDriverObj = driver({
    popoverClass: "cameleon-tutorial-popover",
    stagePadding: 4,
  });

  setDriverObj(newDriverObj);

  return () => {
    if (newDriverObj) {
      newDriverObj.destroy();
    }
  };
}, []);

  useEffect(() => {
    if (currentStep !== 2.5 || !sceneRef.current) return;

    const regions = [];

    const addRegion = (position, isCorrect) => {
      const geometry = new THREE.SphereGeometry(2, 16, 16);
      const material = new THREE.MeshBasicMaterial({
        color: 0xff0000,
        emissive: 0x220000,
        transparent: true,
        opacity: 0.9,
        depthWrite: false,
      });
      const mesh = new THREE.Mesh(geometry, material);
      mesh.position.copy(position);
      mesh.userData.isCorrect = isCorrect;
      sceneRef.current.add(mesh);
      regions.push(mesh);
    };

    addRegion(new THREE.Vector3(0, 0, 10), false);
    addRegion(new THREE.Vector3(12, 80, 1), true);
    addRegion(new THREE.Vector3(-20, 60, -20), false);
    addRegion(new THREE.Vector3(0, 40, 25), false);
    regionsRef.current = regions;

    setHighlightRegions(regions);

    const handleClick = (event) => {
      const rect = rendererRef.current.domElement.getBoundingClientRect();
      pointer.current.x = ((event.clientX - rect.left) / rect.width) * 2 - 1;
      pointer.current.y = -((event.clientY - rect.top) / rect.height) * 2 + 1;
      raycaster.setFromCamera(pointer.current, cameraRef.current);
      const intersects = raycaster.intersectObjects(regions, false);
      if (intersects.length > 0) {
        const selected = intersects[0].object;
        if (selected.userData.isCorrect) {
          setFeedbackMessage(
            "✅ Correct! This part is floating and disconnected."
          );
          setTimeout(() => setCurrentStep(3), 1500);
        } else {
          setFeedbackMessage(
            "❌ Try again — which parts are unsupported layer to layer?"
          );
        }
      }
    };

    const handlePointerMove = (event) => {
      const rect = rendererRef.current.domElement.getBoundingClientRect();
      pointer.current.x = ((event.clientX - rect.left) / rect.width) * 2 - 1;
      pointer.current.y = -((event.clientY - rect.top) / rect.height) * 2 + 1;

      raycaster.setFromCamera(pointer.current, cameraRef.current);
      const intersects = raycaster.intersectObjects(regionsRef.current, false);

      if (intersects.length > 0) {
        const hovered = intersects[0].object;

        if (hovered !== hoveredRegion) {
          // 如果原来有高亮过其他小球，恢复颜色
          if (hoveredRegion) {
            hoveredRegion.material.color.set(
              hoveredRegion.userData.isCorrect ? 0x00ff00 : 0xff0000
            );
          }

          // 设置新高亮对象
          hovered.material.color.set(0xffff00); // 黄色
          setHoveredRegion(hovered);
        }
      } else {
        // 没有任何 hover 到的对象 → 恢复颜色
        if (hoveredRegion) {
          hoveredRegion.material.color.set(
            hoveredRegion.userData.isCorrect ? 0x00ff00 : 0xff0000
          );
          setHoveredRegion(null);
        }
      }
    };

    window.addEventListener("pointerdown", handleClick);
    window.addEventListener("pointermove", handlePointerMove);

    return () => {
      window.removeEventListener("pointerdown", handleClick);
      window.removeEventListener("pointermove", handlePointerMove);
      regions.forEach((mesh) => sceneRef.current.remove(mesh));
    };
  }, [currentStep]);

  return (
    <div className="container">
      <div ref={containerRef} className="canvas-container"></div>

      {!isLoading && currentStep === 1 && (
        <div className="instruction-box">
          <h3>🧭 Step 1: Observation & Diagnosis</h3>
          <p>
            Some parts of the laser-cut object may not be manufacturable.
            Carefully observe the structure and think: which part might be
            problematic?
          </p>
          <p>When you're ready, start identifying the disconnected part.</p>
          <button className="action-button" onClick={() => setCurrentStep(2.5)}>
            Start Diagnosis
          </button>
        </div>
      )}

      {/* Step 2 — 选择交互 */}
      {!isLoading && currentStep === 2.5 && (
        <div className="instruction-box">
          <h3>👆 Step 2: Click to Diagnose</h3>
          <p>Click on the part you think is problematic.</p>
          {feedbackMessage && <div className="feedback">{feedbackMessage}</div>}
        </div>
      )}

{!isLoading && currentStep === 3 && (
  <div className="instruction-box">
    <h3>📘 Step 3: Explanation</h3>
    <p>
      ✅ <strong>Well done!</strong> You correctly identified the problematic part.
    </p>
    <p>
      This part appears to be <strong>floating and disconnected</strong> from the main structure.
      While it may be physically cut by the laser, <strong>it cannot be properly assembled</strong>—there is no connection holding it in place, so it will fall out or shift during use.
    </p>
    <p>
      When designing for fabrication, it's important to ensure that every part is either structurally integrated or mechanically constrained after cutting.
    </p>
    <button className="action-button" onClick={() => setCurrentStep(3.5)}>
      How can I fix this?
    </button>
  </div>
)}


{!isLoading && currentStep === 3.5 && (
  <div className="instruction-box">
    <h3>🛠 Step 3: Suggestion – Fix within Laser Cutting</h3>
    <p>
      🧩 <strong>Option 1:</strong> Stay with <strong>laser cutting</strong>, but modify the geometry.
    </p>

    <div className="interactive-adjustment">
      <label><strong>Try adjusting the number of cuts:</strong></label>
      <div className="input-group">
        <input
          type="range"
          className="slider"
          min={3}
          max={10}
          value={cutNumber}
          onChange={(e) => setCutNumber(parseInt(e.target.value))}
          disabled={isLoading}
        />
        <div className="value">{cutNumber}</div>
      </div>
    </div>

    <p>
      This ensures the part can stay in place during both cutting and assembly. Try different settings to see how the result changes.
    </p>

    <button className="action-button" onClick={() => setCurrentStep(3.52)}>
      Are there better options?
    </button>
  </div>
)}



{!isLoading && currentStep === 3.52 && (
  <div className="instruction-box">
    <h3>🔄 Step 3: Compare Different Workflow Solutions</h3>
    <p>
      🔄 <strong>Option 2:</strong> Use a different fabrication method.
    </p>
    <p>
      Some workflows handle geometric complexity differently — understanding these trade-offs is key to fabrication planning.
    </p>
    
<div style={{ margin: '15px 0' }}>
      <button 
        className="action-button"
        style={{ 
          width: '100%', 
          margin: '10px 0',
          padding: '15px',
          border: '2px solid #9c27b0',
          background: '#f3e5f5'
        }}
        onClick={() => showSolutionComparison('moldmaking')}
      >
        🏺 Mold Making Solution
        <div style={{ fontSize: '12px', color: '#666', marginTop: '5px' }}>
          High resolution, complete shapes → soft silicone material
        </div>
      </button>
      
      <button 
        className="action-button"
        style={{ 
          width: '100%', 
          margin: '10px 0',
          padding: '15px',
          border: '2px solid #3288e6',
          background: '#f0f7ff'
        }}
        onClick={() => showSolutionComparison('stacked-layers')}
      >
        📚 Stacked Layers Approach  
        <div style={{ fontSize: '12px', color: '#666', marginTop: '5px' }}>
          Heavier, more material → better shape preservation
        </div>
      </button>

      <button 
        className="action-button"
        style={{ 
          width: '100%', 
          margin: '10px 0',
          padding: '15px',
          border: '2px solid #4caf50',
          background: '#e8f5e8'
        }}
        onClick={() => showSolutionComparison('3d-printing')}
      >
        🖨️ 3D Printing Solution  
        <div style={{ fontSize: '12px', color: '#666', marginTop: '5px' }}>
          Perfect geometry preservation → rigid plastic material
        </div>
      </button>
    </div>

    <p style={{ fontSize: '14px', fontStyle: 'italic', color: '#666' }}>
      Click on each option to see how it's highlighted in the sidebar and learn about its key advantages.
    </p>

    <button className="action-button" onClick={() => setCurrentStep(4)}>
      I've seen the comparisons
    </button>
  </div>
)}


{!isLoading && currentStep === 4 && (
  <div className="instruction-box">
    <h3>🎉 Congratulations!</h3>
    <p>
      <strong>You've successfully completed Example Task 1!</strong>
    </p>
    <div style={{
      background: "#f0fff0",
      border: "2px solid #28a745",
      borderRadius: "8px",
      padding: "15px",
      margin: "15px 0",
      textAlign: "left"
    }}>
      <h4 style={{ margin: "0 0 10px 0", color: "#28a745" }}>✅ What you learned:</h4>
      <ul style={{ margin: 0, paddingLeft: "20px" }}>
        <li>How different <strong>fabrication workflows</strong> handle complex shapes</li>
        <li>The importance of <strong>design for making</strong></li>
      </ul>
    </div>
  
    <p style={{ color: "#007bff", fontWeight: "bold" }}>
      🎯 You're now ready for more advanced fabrication challenges!
    </p>
  </div>
)}


      {isLoading && <div className="loading-box">Loading model...</div>}
      {error && <div className="error-box">Error: {error}</div>}
    </div>
  );
};

export default Example1FloatingPart;
